import { useEffect, useMemo, useState } from 'react'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import { Button } from '@mui/material'
import { useReadUserProfile } from '@/api/user-manager'
import {
  clearConfirmedSteps,
  useConfirmedSteps,
} from '@/pages/credit/journeys/confirmed-steps'
import {
  isProposalExpired,
  useReadApplication,
} from '@/api/financial/application/read-application'
import {
  usePersonalLoanPreview,
  useSavePersonalLoanSimulation,
} from '@/api/financial/simulation/simulate-personal-loan'
import {
  useCompleteProponents,
  useUpdatePerson,
} from '@/api/financial/person/update-person'
import { useMunicipalities } from '@/api/financial/person/municipalities'
import {
  useDeleteDocument,
  useUploadDocument,
} from '@/api/financial/file/documents'
import {
  useAcceptProposal,
  usePreAnalysis,
  useReissueProposal,
  useSendCommittee,
} from '@/api/financial/committee/committee'
import { JourneySheet } from '@/components/credit-journey/journey-sheet'
import { JourneyShell } from '@/components/credit-journey/journey-shell'
import { useDebounce } from '@/hooks'
import { formatTaxId } from '@/helpers'
import {
  hydrationStamp,
  shouldRehydrate,
} from '@/pages/credit/journeys/rehydrate'
import { routes } from '@/routes'
import { CepResponse } from '@/types'
import {
  PersonalLoanStep,
  isStepComplete,
  personalLoanSteps,
  requiredDocuments,
  resolveStep,
  stageOf,
  stepProgress,
} from './personal-loan-flow'
import {
  PersonalLoanForm,
  clearDraft,
  clearDraftFields,
  emptyForm,
  formFromApplication,
  isStepValid,
  mergeDraft,
  personPayloadForStep,
  readDraft,
  stepFields,
  writeDraft,
} from './personal-loan-form'
import { PersonalLoanScreens } from './personal-loan-screens'
import { JourneySkeleton } from '@/components/credit-journey/journey-skeleton'

const PRODUCT_NAME = 'Crédito para médicos'

const titles: Record<PersonalLoanStep, string> = {
  valor: 'Simule seu crédito',
  eu: 'Seus dados',
  end: 'Onde você mora?',
  civil: 'Estado civil e declarações',
  uso: 'Renda e uso do crédito',
  grad: 'Sua graduação',
  espec: 'Residência e pós-graduação',
  conta: 'Onde você quer receber?',
  docs: 'Envie os documentos',
  analise: '',
  proposta: 'Seu crédito foi aprovado!',
  assinar: 'Assine a CCB',
  fim: '',
}

const hints: Partial<Record<PersonalLoanStep, string>> = {
  grad: 'Dados da sua formação em Medicina.',
  espec: 'Se não tiver, é só continuar.',
  conta:
    'O dinheiro cai nesta conta depois da assinatura. Ela precisa estar no seu CPF.',
  proposta: 'Confira a proposta antes de aceitar.',
  assinar:
    'Só falta essa etapa. Depois da assinatura, enviamos o dinheiro para a sua conta.',
}

const nextLabels: Partial<Record<PersonalLoanStep, string>> = {
  conta: 'Salvar e ir para documentos',
  docs: 'Enviar para análise',
  analise: 'Ir para o Resumo',
  proposta: 'Aceitar proposta',
  assinar: 'Assinar contrato',
  fim: 'Ir para o Resumo',
}

export function PersonalLoanJourneyPage() {
  const { application_key: applicationKey = '', step: stepParam } = useParams()
  const navigate = useNavigate()
  const application = useReadApplication(applicationKey)
  const user = useReadUserProfile()
  const [form, setForm] = useState<PersonalLoanForm>(emptyForm)
  const [hydratedStamp, setHydratedStamp] = useState<string | null>(null)
  const [addressState, setAddressState] = useState('')
  const [ibge, setIbge] = useState('')
  const [exitOpen, setExitOpen] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [saving, setSaving] = useState<'saving' | 'saved' | 'error'>('saved')
  const { confirmed, confirm } = useConfirmedSteps(applicationKey)

  const municipalities = useMunicipalities(addressState)
  const data = application.data
  const person = data?.application_persons[0]?.person
  const stamp = hydrationStamp(applicationKey, person?.key)
  const hydrated = hydratedStamp?.startsWith(`${applicationKey}:`) ?? false
  const crmNumber = user.data?.profile?.crm
  const crmUf = user.data?.profile?.crm_uf
  const crm = crmNumber
    ? crmUf
      ? `CRM-${crmUf} ${crmNumber}`
      : crmNumber
    : null

  useEffect(() => {
    if (!data || !shouldRehydrate(hydratedStamp, stamp)) return
    const draft = readDraft(applicationKey)
    setForm(mergeDraft(formFromApplication(data), draft))
    const stateCode =
      draft.address_state ||
      data.application_persons[0]?.person.address[0]?.municipality?.state
    if (stateCode) setAddressState(stateCode)
    setHydratedStamp(stamp)
  }, [applicationKey, data, hydratedStamp, stamp])

  useEffect(() => {
    const match = municipalities.data?.find((item) => item.ibge_code === ibge)
    if (!match) return
    const municipalityId = String(match.id)
    setForm((current) => ({ ...current, municipality_id: municipalityId }))
    writeDraft(applicationKey, { municipality_id: municipalityId })
  }, [applicationKey, municipalities.data, ibge])

  const facts = useMemo(() => {
    const documents = person?.documents?.map((item) => item.type) ?? []
    const completed = Object.fromEntries(
      personalLoanSteps.map((item) => [
        item,
        // dados já cadastrados só contam depois de conferidos nesta solicitação
        isStepComplete(item, person, {
          hasSimulation: Boolean(data?.simulation),
          documents,
        }) &&
          (stageOf(item) !== 1 || confirmed.includes(item)),
      ]),
    )
    return {
      hasSimulation: Boolean(data?.simulation),
      status: data?.status ?? 'incomplete',
      serverStep: data?.step ?? 'pre_analysis',
      completed,
    }
  }, [data, person, confirmed])

  const step = data ? resolveStep(stepParam, facts) : 'valor'

  useEffect(() => {
    if (!data || stepParam === step) return
    navigate(`/credit/applications/${applicationKey}/${step}`, {
      replace: true,
    })
  }, [data, step, stepParam, applicationKey, navigate])

  // o cálculo do valor e do prazo espera o cliente parar de mexer no controle
  const amount = useDebounce(form.amount, 300)
  const installments = useDebounce(form.installments, 300)
  const [dueDates, setDueDates] = useState<string[]>()
  // só pede um vencimento que o servidor já ofereceu: os vencimentos mudam com o dia
  const firstDue = dueDates?.includes(form.first_due_date)
    ? form.first_due_date
    : undefined
  const preview = usePersonalLoanPreview(
    step === 'valor'
      ? { amount, installments, first_due_date: firstDue }
      : null,
  )
  const offered = preview.data?.limits?.due_dates
  useEffect(() => {
    if (offered) setDueDates(offered)
  }, [offered])

  // vencimentos dependem do dia: um vencimento salvo que saiu das opções é descartado
  useEffect(() => {
    if (step !== 'valor' || !dueDates?.length || !hydrated) return
    if (form.first_due_date && !dueDates.includes(form.first_due_date)) {
      setForm((current) => ({ ...current, first_due_date: '' }))
    }
  }, [step, dueDates, form.first_due_date, hydrated])

  const saveSimulation = useSavePersonalLoanSimulation(applicationKey)
  const preAnalysis = usePreAnalysis(applicationKey)
  const updatePerson = useUpdatePerson(applicationKey, person?.key ?? '')
  const complete = useCompleteProponents(applicationKey)
  const upload = useUploadDocument(applicationKey)
  const removeDocument = useDeleteDocument(applicationKey)
  const send = useSendCommittee(applicationKey)
  const accept = useAcceptProposal(applicationKey)
  const reissue = useReissueProposal(applicationKey)
  const proposal = data?.committee?.proposal ?? null
  const proposalExpired = proposal ? isProposalExpired(proposal) : false

  function patch(partial: Partial<PersonalLoanForm>) {
    setForm((current) => ({ ...current, ...partial }))
    writeDraft(applicationKey, partial)
  }

  function go(nextStep: PersonalLoanStep) {
    navigate(`/credit/applications/${applicationKey}/${nextStep}`)
  }

  function back() {
    const index = personalLoanSteps.indexOf(step)
    if (index > 0) go(personalLoanSteps[index - 1])
  }

  const attached = new Set(person?.documents?.map((item) => item.type) ?? [])
  const phoneVerified = Boolean(
    user.data?.alias?.find((alias) => alias.type === 'phone' && alias.verified),
  )
  const previewReady =
    Boolean(preview.data) &&
    !preview.isFetching &&
    preview.data?.summary.phases[0]?.installments === form.installments &&
    preview.data?.summary.requested === form.amount &&
    preview.data?.first_due_date === form.first_due_date
  const nextDisabled = !isStepValid(step, form, {
    hasPreview: previewReady,
    hasRequiredDocs: requiredDocuments.every((type) => attached.has(type)),
    hasProposal: Boolean(proposal && !proposal.accepted_at && !proposalExpired),
    denied: data?.status === 'denied',
    hasVerifiedPhone: phoneVerified,
  })

  async function sendToAnalysis() {
    setConfirmOpen(false)
    try {
      setSaving('saving')
      await send.mutateAsync()
      await application.refetch()
      clearDraft(applicationKey)
      clearConfirmedSteps(applicationKey)
      setSaving('saved')
      go('analise')
    } catch {
      setSaving('error')
    }
  }

  async function next() {
    if (step === 'analise' || step === 'fim') {
      clearDraft(applicationKey)
      navigate(routes.credit.index)
      return
    }
    if (step === 'docs') {
      setConfirmOpen(true)
      return
    }
    const following = personalLoanSteps[personalLoanSteps.indexOf(step) + 1]
    const personPayload = person ? personPayloadForStep(step, form, crm) : null
    try {
      setSaving('saving')
      if (step === 'valor') {
        await saveSimulation.mutateAsync({
          amount: form.amount,
          installments: form.installments,
          first_due_date: form.first_due_date,
          crm,
        })
        await preAnalysis.mutateAsync()
        const refreshed = await application.refetch()
        clearDraftFields(applicationKey, stepFields.valor ?? [])
        go(refreshed.data?.status === 'denied' ? 'analise' : 'eu')
      } else if (step === 'conta' && personPayload) {
        await updatePerson.mutateAsync(personPayload)
        clearDraftFields(applicationKey, stepFields.conta ?? [])
        try {
          await complete.mutateAsync()
        } catch (error) {
          const details = (error as { details?: { step?: PersonalLoanStep } })
            .details
          await application.refetch()
          if (details?.step) {
            go(details.step)
            setSaving('saved')
            return
          }
          throw error
        }
        await application.refetch()
        go('docs')
      } else if (personPayload) {
        await updatePerson.mutateAsync(personPayload)
        await application.refetch()
        clearDraftFields(applicationKey, stepFields[step] ?? [])
        confirm(step)
        if (following) go(following)
      } else if (step === 'proposta' && proposal) {
        await accept.mutateAsync(proposal.key)
        await application.refetch()
        go('assinar')
      } else if (following) {
        go(following)
      }
      setSaving('saved')
    } catch {
      setSaving('error')
    }
  }

  if (!applicationKey) return <Navigate to={routes.credit.index} replace />
  if (application.isLoading || !data || !hydrated || user.isLoading) {
    return <JourneySkeleton />
  }
  if (data.product !== 'personal_loan') {
    return <Navigate to={routes.credit.index} replace />
  }

  const name =
    [user.data?.first_name, user.data?.last_name].filter(Boolean).join(' ') ||
    person?.name ||
    ''
  const emails =
    user.data?.alias?.filter((alias) => alias.type === 'email') ?? []
  const email =
    (emails.find((alias) => alias.verified) ?? emails[0])?.data ?? ''
  const phone =
    user.data?.alias?.find((alias) => alias.type === 'phone')?.data ?? ''
  const denied = data.status === 'denied'

  return (
    <>
      <JourneyShell
        productName={PRODUCT_NAME}
        stage={stageOf(step)}
        progress={stepProgress(step)}
        title={titles[step]}
        hint={hints[step]}
        done={step === 'fim'}
        onBack={
          personalLoanSteps.indexOf(step) > 0 &&
          step !== 'analise' &&
          step !== 'fim'
            ? back
            : undefined
        }
        onExit={() =>
          stageOf(step) < 3 ? setExitOpen(true) : navigate(routes.credit.index)
        }
        onNext={next}
        nextLabel={nextLabels[step] ?? 'Continuar'}
        nextDisabled={nextDisabled}
        saving={saving}
        showSaved={stageOf(step) < 2}
      >
        <PersonalLoanScreens
          step={step}
          form={form}
          patch={patch}
          preview={preview.data}
          previewLoading={preview.isFetching}
          identity={{
            name,
            taxId: formatTaxId(user.data?.tax_id ?? ''),
            crm: crm ?? person?.crm ?? '',
            email,
            phone,
          }}
          municipalities={municipalities.data ?? []}
          addressState={addressState}
          onStateChange={(stateCode) => {
            setAddressState(stateCode)
            setIbge('')
            patch({ municipality_id: '' })
            writeDraft(applicationKey, { address_state: stateCode })
          }}
          onCep={(cep: CepResponse) => {
            setAddressState(cep.state_code)
            writeDraft(applicationKey, { address_state: cep.state_code })
            setIbge(cep.ibge_code)
            patch({
              zip_code: cep.zipcode,
              street: cep.street,
              district: cep.district,
            })
          }}
          documents={person?.documents ?? []}
          onUpload={(type, file) => {
            if (!person) return
            upload.mutate({ type, file, personKey: person.key })
          }}
          onRemove={(documentKey) => removeDocument.mutateAsync(documentKey)}
          uploadingType={upload.isPending ? upload.variables?.type : undefined}
          phoneVerified={phoneVerified}
          denied={denied}
          deniedAt={data.denied_at ?? null}
          proposal={proposal}
          proposalExpired={proposalExpired}
          reissuing={reissue.isPending}
          onReissue={async () => {
            if (!proposal) return
            await reissue.mutateAsync(proposal.key)
            await application.refetch()
            go('analise')
          }}
        />
      </JourneyShell>

      <JourneySheet
        open={confirmOpen}
        title="Importante!"
        description="Você enviou seus 3 últimos extratos pessoa física em PDF? Essa documentação é a que mais costuma atrasar o resultado da sua análise."
        onClose={() => setConfirmOpen(false)}
      >
        <Button
          variant="contained"
          disabled={send.isPending}
          onClick={sendToAnalysis}
        >
          Sim, enviei
        </Button>
        <Button variant="text" onClick={() => setConfirmOpen(false)}>
          Voltar
        </Button>
      </JourneySheet>

      <JourneySheet
        open={exitOpen}
        title="Sair da solicitação?"
        description="Tudo o que você preencheu fica salvo. Para continuar, abra Solicitações."
        onClose={() => setExitOpen(false)}
      >
        <Button
          variant="contained"
          onClick={() => navigate(routes.credit.applications)}
        >
          Sair e continuar depois
        </Button>
        <Button variant="text" onClick={() => setExitOpen(false)}>
          Continuar preenchendo
        </Button>
      </JourneySheet>
    </>
  )
}
